import { useCallback } from 'react';

import { MedicalInformationType } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { useBoolean } from '@dreryk/react-common';

import { MedicalImagingEntryFormBean } from '../medicalImaging/MedicalImagingEntryForm.types';

import { MedicalInformationAddEntryModal } from './MedicalInformationAddEntryModal';
import { MedicalInformationAddEntryModalContent } from './MedicalInformationAddEntryModal.content';
import { MedicalInformationApproveDraftSubmitValues } from './MedicalInformationAddEntryModal.hooks';
import { MedicalInformationOnSuccessCallback } from './MedicalInformationAddEntryModal.types';

type MedicalInformationTypesForSuccessCallbackWithDefaultParameters =
   | 'ALLERGY'
   | 'ANALYTICAL_TEST_RESULT'
   | 'ANTHROPOMETRY'
   | 'HOSPITALIZATION'
   | 'MEDICAL_CONSULTATION'
   | 'OTHER'
   | 'REFERRAL'
   | 'REHABILITATION'
   | 'RISK_FACTOR'
   | 'TREATMENT';
type MedicalInformationTypesForSuccessCallbackWithVoidParameters =
   | 'CHRONIC_DISEASE'
   | 'DIAGNOSIS'
   | 'GYNECOLOGICAL_INTERVIEW';

type SuccessCallbackParametersMapByMedicalInformationType = {
   MEDICAL_IMAGING: MedicalImagingEntryFormBean;
} & Record<MedicalInformationTypesForSuccessCallbackWithDefaultParameters, MedicalInformationApproveDraftSubmitValues> &
   Record<MedicalInformationTypesForSuccessCallbackWithVoidParameters, undefined>;

type MedicalInformationAddEntryModalOpenerRenderProps = {
   openModal: VoidFunction;
};

type MedicalInformationAddEntryModalOpenerProps<TYPE extends MedicalInformationType> = {
   readonly availableMedicalInformationTypes?: TYPE[];
   readonly patientId: number;
   readonly children: (renderProps: MedicalInformationAddEntryModalOpenerRenderProps) => JSX.Element;
   readonly onSuccess?: (
      medicalInformationId: number,
      medicalInformationType: TYPE,
      parameters: TYPE extends MedicalInformationTypesForSuccessCallbackWithVoidParameters
         ? never
         : SuccessCallbackParametersMapByMedicalInformationType[TYPE]
   ) => void;
};

export const MedicalInformationAddEntryModalOpener = <TYPE extends MedicalInformationType>(
   props: MedicalInformationAddEntryModalOpenerProps<TYPE>
): JSX.Element => {
   const [{ setFalse: closeModal, setTrue: openModal }, modalOpened] = useBoolean(false);

   const handleSuccess = useCallback(
      (medicalInformationId: number, medicalInformationType: TYPE): MedicalInformationOnSuccessCallback => (
         parameters
      ) => {
         props.onSuccess?.(
            medicalInformationId,
            medicalInformationType,
            parameters as TYPE extends MedicalInformationTypesForSuccessCallbackWithVoidParameters
               ? never
               : SuccessCallbackParametersMapByMedicalInformationType[TYPE]
         );

         closeModal();
      },
      [closeModal, props]
   );

   return (
      <>
         {props.children({
            openModal: openModal,
         })}

         {modalOpened ? (
            <MedicalInformationAddEntryModal
               patientId={props.patientId}
               onClose={closeModal}
               availableMedicalInformationTypes={props.availableMedicalInformationTypes}
            >
               {({ medicalInformationDraft, medicalInformationType, onClose }) => (
                  <>
                     {medicalInformationDraft && medicalInformationType ? (
                        <MedicalInformationAddEntryModalContent
                           onClose={onClose}
                           onSuccess={handleSuccess(medicalInformationDraft.id, medicalInformationType as TYPE)}
                           patientId={props.patientId}
                           medicalInformationId={medicalInformationDraft.id}
                           medicalInformationType={medicalInformationType}
                        />
                     ) : null}
                  </>
               )}
            </MedicalInformationAddEntryModal>
         ) : null}
      </>
   );
};
